// Reporting canvas layout — PURE, and a LEAF module (imports nothing from the
// app's module graph, only its own types).
//
// Both reporting dashboards render their whole top section — KPI TILES, CHARTS,
// TABLES and the user's own WIDGETS — through ONE shared draggable / resizable
// shell (`src/modules/reporting/ReportingChartGrid.tsx`): a single CANVAS on
// which every panel is freely placeable. This file is the arrangement half of
// that: the encode/decode, the default arrangement, and the reconcile that keeps
// a stored layout honest against the panels actually on screen. Keeping it pure
// + leaf means the two dashboards' persistence modules (chartPrefs.ts on Meta,
// googleAdsViewPrefs.ts on Google) can both import it with no risk of the
// circular-import class of bug googleAdsChartCatalog.ts documents.
//
// PERSISTENCE SHAPE. The layout rides the SAME `User.gridPreferences` envelope
// each dashboard already uses, as tokens in the reserved preset's
// `visibleColumns` alongside the existing `lvl:` / `col:` / `chart:` / `style:`
// tokens — no server change, no second save mechanism:
//
//     can:<kind>:<id>:<x>:<y>:<w>:<h>
//
// ⚠️⚠️ THE PANEL KEY CARRIES ITS KIND, and that is what keeps four populations
// apart in ONE grid. `trend` is simultaneously a Google CHART key and a
// perfectly ordinary metric key (so a TILE key); a widget id is arbitrary. Keyed
// bare, dragging one would silently move another. So every key is built by
// `canvasPanelKey(kind, id)` → `tile:cost`, `chart:trend`, `table:searchTerms`,
// `widget:w1`, and a key that is NOT kind-qualified with a KNOWN kind is
// dropped on decode rather than admitted into the layout.
//
// ⚠️ THE KIND MATCH IS AN EXACT SEGMENT, NEVER A HEAD SUBSTRING. `tile` and
// `table` share their first two characters and `widget` shares one with neither
// — but the failure mode is a `startsWith` that treats one kind as a prefix of
// another, which is exactly the bug the old per-region prefixes were written to
// avoid. `parseCanvasPanelKey` splits at the FIRST colon and matches the segment
// whole; a test spans all four kinds against each other.
//
// ⚠️ THE `can:` PREFIX IS A DELIBERATE ONE-TIME RESET. The previous build stored
// FOUR independent per-region layouts as `lay:<region>:…`, each with its own
// coordinate space starting at y=0. Those coordinates are meaningless on a
// single shared canvas — decoding them would pile four overlapping stacks on top
// of each other, which reads as broken. A different prefix makes every `lay:`
// token decode to nothing, so a user with a saved arrangement gets the new
// default once and re-drags in a few seconds. That beats a migration for a
// preference days old, and it is the same trade the `lay:` prefix itself made.
//
// ⚠️ ONE LAYOUT ONLY — the desktop `lg` breakpoint. Narrow viewports fall back
// to react-grid-layout's single-column auto-stack, which needs nothing stored.
// These are desktop tools; multi-breakpoint persistence would be complexity with
// no payoff, and a stored phone layout nobody ever arranges is a liability.
//
// Nothing here throws. A malformed token is DROPPED and that panel falls back to
// its default slot — the same posture as googleAdsViewPrefs / chartPrefs, where
// a garbled pref must degrade to a sane view rather than break the page.

/**
 * What KIND of thing a canvas panel is. Not a grouping — every panel lives in
 * the one grid and can sit anywhere — purely the namespace that keeps a tile's
 * key, a chart's key, a table's key and a widget id from colliding.
 *
 * ⚠️ Adding a kind is exactly this one line; nothing else in here is
 * kind-specific, which is the point.
 */
export type CanvasPanelKind = 'tile' | 'chart' | 'table' | 'widget';

const CANVAS_PANEL_KINDS: readonly CanvasPanelKind[] = ['tile', 'chart', 'table', 'widget'];

/**
 * The canvas key for a panel. The ONE place a key is composed — so a caller
 * cannot accidentally hand the grid a bare, collidable key.
 */
export function canvasPanelKey(kind: CanvasPanelKind, id: string): string {
  return `${kind}:${id}`;
}

/**
 * Split a canvas key back into its kind and id, or null when it is not
 * kind-qualified with a KNOWN kind.
 *
 * Splits at the FIRST colon, so the id keeps any further colons (a future
 * colon-bearing metric key still resolves) — and matches the kind as a whole
 * segment, never as a prefix.
 */
export function parseCanvasPanelKey(
  key: string,
): { kind: CanvasPanelKind; id: string } | null {
  const sep = key.indexOf(':');
  if (sep <= 0) return null;
  const kind = key.slice(0, sep);
  const id = key.slice(sep + 1);
  if (!id) return null;
  if (!CANVAS_PANEL_KINDS.includes(kind as CanvasPanelKind)) return null;
  return { kind: kind as CanvasPanelKind, id };
}

/** One panel's placement on the 12-column grid. */
export interface ChartPanelLayout {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * What a dashboard declares about a panel it is CURRENTLY showing.
 *
 * The default width/height are the dashboard's business, not this module's:
 * Google's single-metric chart is 8 columns wide when the competitiveness block
 * sits beside it and 12 when it doesn't — reproducing exactly what the page
 * renders today. Passing that in keeps `defaultChartLayout` a generic packer.
 */
export interface ChartPanelSpec {
  key: string;
  /** Columns wide by default (clamped to 1..CHART_GRID_COLS). */
  defaultW?: number;
  /** Rows tall by default. */
  defaultH?: number;
  /** Smallest the user may resize it to. */
  minW?: number;
  minH?: number;
}

/** The grid is 12 columns, like every other 12-column layout in the app. */
export const CHART_GRID_COLS = 12;

/**
 * Row height in px, and the gap between items. A panel of h=8 is therefore
 * 8*30 + 7*16 = 352px tall — near enough to today's chart card (a 256px canvas
 * plus its header and padding) that the default layout reproduces the current
 * appearance rather than resizing everything on first load.
 */
export const CHART_GRID_ROW_HEIGHT = 30;
export const CHART_GRID_MARGIN: [number, number] = [16, 16];

export const CHART_PANEL_DEFAULT_H = 8;

/**
 * The DEFAULT interaction minimum for a panel — what the shell enforces while
 * dragging when a panel declares no `minW`/`minH` of its own. KPI tiles declare
 * smaller ones (a tile may legitimately be narrower than a chart).
 */
export const CHART_PANEL_MIN_W = 3;
export const CHART_PANEL_MIN_H = 4;

/**
 * ⚠️ The ABSOLUTE floor, used when DECODING — deliberately lower than the
 * interaction minimum above. Decoding is storage, not interaction: it must
 * round-trip whatever a drag legitimately produced, and a tile whose own `minW`
 * is 2 can be stored at 2. Clamping storage to the chart-sized default would
 * silently widen every narrow tile on reload. Only a zero-sized panel — which is
 * unusable and un-draggable back — is floored.
 */
const ABS_MIN = 1;

const TOKEN_PREFIX = 'can:';

/** True for a real, non-negative, finite integer — the only thing a coordinate may be. */
function isCoord(n: number): boolean {
  return Number.isInteger(n) && n >= 0;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Encode the canvas layout as `can:` tokens, in the given order.
 *
 * A key that is not kind-qualified is REFUSED rather than written — encoding
 * must never produce a token its own decoder would drop.
 */
export function encodeCanvasLayout(layout: readonly ChartPanelLayout[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of layout) {
    if (!item.key || seen.has(item.key)) continue;
    if (!parseCanvasPanelKey(item.key)) continue;
    if (!isCoord(item.x) || !isCoord(item.y) || !isCoord(item.w) || !isCoord(item.h)) continue;
    seen.add(item.key);
    out.push(`${TOKEN_PREFIX}${item.key}:${item.x}:${item.y}:${item.w}:${item.h}`);
  }
  return out;
}

/**
 * Decode the canvas tokens back into a layout. Every other view-state token
 * sharing the preset is ignored, a malformed one is dropped, and a duplicate key
 * keeps the FIRST occurrence.
 *
 * Out-of-range but well-formed numbers are CLAMPED rather than dropped: a panel
 * that somehow stored w=40 should come back a sensible width, not vanish.
 */
export function decodeCanvasLayout(tokens: readonly string[]): ChartPanelLayout[] {
  const seen = new Set<string>();
  const out: ChartPanelLayout[] = [];
  for (const token of tokens) {
    // ⚠️ `can:` and not `lay:` — a stored per-region layout from the previous
    // build falls through to the unified default, deliberately (see the header).
    if (!token.startsWith(TOKEN_PREFIX)) continue;
    const parts = token.slice(TOKEN_PREFIX.length).split(':');
    // <kind>:<id> + x + y + w + h — so at least 6 segments. Split from the RIGHT
    // so an id containing a colon still resolves; the same posture as `tstyle:`.
    if (parts.length < 6) continue;
    const key = parts.slice(0, parts.length - 4).join(':');
    if (!key || seen.has(key)) continue;
    // ⚠️ The kind gate. An un-namespaced or unknown-kind key never enters the
    // layout, so the four populations cannot collide inside the one grid.
    if (!parseCanvasPanelKey(key)) continue;
    const [xs, ys, ws, hs] = parts.slice(parts.length - 4);
    const x = Number(xs);
    const y = Number(ys);
    const w = Number(ws);
    const h = Number(hs);
    if (!isCoord(x) || !isCoord(y) || !isCoord(w) || !isCoord(h)) continue;
    // A zero-sized panel is unusable and cannot be dragged back, so the floor
    // carries real meaning — but it is the ABSOLUTE floor, not the chart-sized
    // interaction default (see ABS_MIN).
    const cw = clamp(w, ABS_MIN, CHART_GRID_COLS);
    seen.add(key);
    out.push({
      key,
      x: clamp(x, 0, CHART_GRID_COLS - cw),
      y,
      w: cw,
      h: Math.max(h, ABS_MIN),
    });
  }
  return out;
}

/**
 * The DEFAULT arrangement — what a user who has never dragged sees, and what
 * "Reset layout" restores. Panels are packed left-to-right in the order given,
 * wrapping to a new row when the next one doesn't fit.
 *
 * ⚠️ THIS IS WHAT REPRODUCES THE OLD STACKED LOOK ON THE UNIFIED CANVAS. The
 * page supplies the union in DISPLAY order (tiles, then charts, then tables,
 * then widgets) at the sizes each used to have, and the packer lays them out in
 * exactly the rows the four separate regions used to render — so collapsing four
 * grids into one changes nothing on screen until the user drags something. It is
 * also, therefore, the safety rail behind Reset: the one guaranteed way back to
 * a sensible arrangement on a free canvas.
 */
export function defaultChartLayout(panels: readonly ChartPanelSpec[]): ChartPanelLayout[] {
  const out: ChartPanelLayout[] = [];
  let x = 0;
  let rowY = 0;
  let rowH = 0;
  for (const panel of panels) {
    const w = clamp(panel.defaultW ?? CHART_GRID_COLS, ABS_MIN, CHART_GRID_COLS);
    const h = Math.max(panel.defaultH ?? CHART_PANEL_DEFAULT_H, ABS_MIN);
    if (x + w > CHART_GRID_COLS) {
      // Doesn't fit beside what's already on this row — start the next one below
      // the TALLEST item on it, so a short panel can't leave a gap a tall
      // neighbour then overlaps.
      rowY += rowH;
      x = 0;
      rowH = 0;
    }
    out.push({ key: panel.key, x, y: rowY, w, h });
    x += w;
    rowH = Math.max(rowH, h);
  }
  return out;
}

/** The row immediately below everything in a layout. */
function bottomOf(layout: readonly ChartPanelLayout[]): number {
  return layout.reduce((n, i) => Math.max(n, i.y + i.h), 0);
}

/**
 * Resolve the layout ACTUALLY rendered, given what was stored and which panels
 * are currently on screen. Called on every render — the chart menu can turn a
 * panel on or off at any time, so the stored layout is never assumed to match.
 *
 * · A stored panel that is still shown keeps its exact placement.
 * · A newly-shown panel with nothing stored is APPENDED below everything, so it
 *   appears somewhere obvious rather than colliding with an existing panel.
 * · A stored panel that is no longer shown is simply absent from the result —
 *   its token survives elsewhere (see `mergeStoredLayout`), so re-enabling the
 *   chart puts it back where the user left it.
 * · Nothing stored at all (a legacy or cleared pref) falls back WHOLESALE to the
 *   default arrangement, not to a column of appended slots — otherwise every
 *   existing user's first visit would look rearranged.
 */
export function reconcileLayout(
  stored: readonly ChartPanelLayout[],
  panels: readonly ChartPanelSpec[],
): ChartPanelLayout[] {
  const byKey = new Map(stored.map((i) => [i.key, i]));
  const anyStored = panels.some((p) => byKey.has(p.key));
  if (!anyStored) return defaultChartLayout(panels);

  const placed: ChartPanelLayout[] = [];
  for (const panel of panels) {
    const hit = byKey.get(panel.key);
    if (hit) placed.push({ ...hit });
  }
  let nextY = bottomOf(placed);

  const out: ChartPanelLayout[] = [];
  for (const panel of panels) {
    const hit = byKey.get(panel.key);
    if (hit) {
      out.push({ ...hit });
      continue;
    }
    const w = clamp(panel.defaultW ?? CHART_GRID_COLS, ABS_MIN, CHART_GRID_COLS);
    const h = Math.max(panel.defaultH ?? CHART_PANEL_DEFAULT_H, ABS_MIN);
    out.push({ key: panel.key, x: 0, y: nextY, w, h });
    nextY += h;
  }
  return out;
}

/**
 * Fold a freshly-dragged layout (current panels only) back into the stored one
 * before persisting.
 *
 * Two jobs, both load-bearing:
 * · KEEP the placement of panels that are currently HIDDEN, so toggling a chart
 *   off and back on restores where the user put it rather than dumping it at the
 *   bottom.
 * · PRUNE keys outside the dashboard's catalogue, so a renamed or retired panel
 *   can't leave a token accumulating in the preferences envelope forever.
 */
export function mergeStoredLayout(
  stored: readonly ChartPanelLayout[],
  next: readonly ChartPanelLayout[],
  knownKeys: readonly string[],
): ChartPanelLayout[] {
  const known = new Set(knownKeys);
  const byKey = new Map<string, ChartPanelLayout>();
  for (const item of stored) {
    if (known.has(item.key)) byKey.set(item.key, { ...item });
  }
  for (const item of next) {
    if (known.has(item.key)) byKey.set(item.key, { ...item });
  }
  // Stable order: catalogue order, so the token list doesn't churn between saves.
  return knownKeys.filter((k) => byKey.has(k)).map((k) => byKey.get(k)!);
}
